import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateResultItem,
  EstateResultItemType,
  useTaskCredit_GetCurrentTaskCreditQuery,
} from 'queries';

import { Button } from 'common/button';
import Spinner from 'common/spinner';
import PaymentForm from 'pages/common/estate/payment-form';
import AddressAsset from 'pages/estate-results-page/components/address-asset';
import BankruptcyAsset from 'pages/estate-results-page/components/bankruptcy-asset';
import CollectionAsset from 'pages/estate-results-page/components/collection-asset';
import EmploymentAsset from 'pages/estate-results-page/components/employment-asset';
import NameAsset from 'pages/estate-results-page/components/name-asset';
import TradeAsset from 'pages/estate-results-page/components/trade-asset';
import { EstateResultItemInfo } from 'utils/estate';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  estateResultItem: Partial<EstateResultItem>;
  fullCredits: number;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onPaymentComplete: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setShowModal: Function;
};

const useStyles = makeStyles((theme) => ({
  modalContentWrapper: {
    marginTop: '20px',
    marginBottom: '20px',
    backgroundColor: 'rgba(240, 243, 246, 0.7)',
    padding: '25px',
    paddingTop: '20px',
    '& input': {
      background: 'white',
    },
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
  modalButtonWrapper: {
    marginBottom: '20px',
  },
  assetTitle: {
    color: '#2274A5',
  },
  assetWrapper: {
    border: '2px solid #44D2F2',
    marginTop: '10px',
  },
}));

export default function EstateResultItemPaymentForm({
  estateResultItem,
  onPaymentComplete,
  setShowModal,
}: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: currentTaskCreditData, loading: currentTaskCreditLoading } =
    useTaskCredit_GetCurrentTaskCreditQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting current task credit: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!currentTaskCreditData || currentTaskCreditLoading) {
    return <Spinner />;
  }

  const handlePaymentComplete = () => {
    setShowModal(false);
    onPaymentComplete && onPaymentComplete();
    showToast({
      message: 'Task assigned to Trustate.',
      severity: NotificationSeverity.SUCCESS,
    });
  };

  let bodyContent: JSX.Element | undefined = undefined;
  if (estateResultItem.essentialData) {
    const fields = JSON.parse(estateResultItem.essentialData!);
    bodyContent = (
      <div>
        <Typography className={classes.assetTitle} variant="h6">
          {EstateResultItemInfo[estateResultItem.type!]}
        </Typography>
        <div className={classes.assetWrapper}>
          {estateResultItem.type === EstateResultItemType.Trade ? (
            <TradeAsset
              id={estateResultItem.id!}
              showAssetBtn={false}
              trade={fields}
              title={estateResultItem.title!}
              subtitle={estateResultItem.subtitle!}
            />
          ) : estateResultItem.type === EstateResultItemType.Address ? (
            <AddressAsset
              id={estateResultItem.id!}
              showAssetBtn={false}
              address={fields}
              title={estateResultItem.title!}
              subtitle={estateResultItem.subtitle!}
            />
          ) : estateResultItem.type === EstateResultItemType.Collection ? (
            <CollectionAsset
              id={estateResultItem.id!}
              showAssetBtn={false}
              collection={fields}
              title={estateResultItem.title!}
              subtitle={estateResultItem.subtitle!}
            />
          ) : estateResultItem.type === EstateResultItemType.Employment ? (
            <EmploymentAsset
              id={estateResultItem.id!}
              showAssetBtn={false}
              employment={fields}
              title={estateResultItem.title!}
              subtitle={estateResultItem.subtitle!}
            />
          ) : estateResultItem.type === EstateResultItemType.Bankruptcy ? (
            <BankruptcyAsset
              id={estateResultItem.id!}
              showAssetBtn={false}
              bankruptcy={fields}
              title={estateResultItem.title!}
            />
          ) : estateResultItem.type === EstateResultItemType.Name ? (
            <NameAsset
              id={estateResultItem.id!}
              showAssetBtn={false}
              name={fields}
              title={estateResultItem.title!}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.modalContentWrapper}>
        <PaymentForm
          allowPaymentQuantity={false}
          bodyContent={bodyContent}
          estateResultItemId={estateResultItem.id}
          hasPayment={!!estateResultItem.estateWorkItemId}
          formName="estate-result-item-form"
          handlePaymentComplete={handlePaymentComplete}
          title="Assign Task to Trustate"
          portalId={portalId}
        />
      </div>
      <div className={classes.modalButtonWrapper}>
        <Button
          className={classes.cancelButton}
          color="primary"
          onClick={() => {
            setShowModal(false);
          }}
        >
          Cancel
        </Button>{' '}
        <Button color="secondary" type="submit" form="estate-result-item-form">
          Assign Task
        </Button>
      </div>
    </>
  );
}
